/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
 */

import React from 'react'
import moment from 'moment'
import R from 'ramda'
import autoBind from 'react-autobind'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { injectIntl } from 'react-intl'

import { Chip, FontIcon, IconButton, Badge, CircularProgress } from 'material-ui'
import { white } from 'material-ui/styles/colors'

import * as constants from 'constants/Prestation'
import * as headerConstants from 'constants/Header'
import * as loginConstants from 'constants/Login'
import * as actions from 'actions/ApplicantPrestation'
import * as documentActions from 'actions/Document'

import 'resources/styles/applicant/Dashboard.css'
import ImageDisplay from 'components/common/ImageDisplay'

const getConsumerText = (consumer) => (
  R.path([0, 'entities', 0], consumer) &&
  R.path([0, 'identities', 0], consumer)
    ? `${consumer[0].identities[0].first_name} ${consumer[0].identities[0].last_name} - ${consumer[0].entities[0].name}`
    : R.path([0, 'identities', 0], consumer)
      ? `${consumer[0].identities[0].first_name} ${consumer[0].identities[0].last_name}`
      : ''
)

class List extends React.Component {
  constructor (props) {
    super(props)

    autoBind(this)

    this.state = {
      selected: null,
      prestations: props.prestations,
      isModalOpen: false,
      modalLoading: undefined,
      showDocument: false,
      documentObj: undefined,
    }

    this.openModal = this.openModal.bind(this)
    this.handleDocumentModal = this.handleDocumentModal.bind(this)
  }

  async openModal(prestationUid, prestation, index) {
    if (this.props.modalLoading) return

    try {
      this.setState({ modalLoading: prestationUid })

      const obj = this.state.prestations[index]

      if (obj.document_count === 1) {
        const request = await documentActions.getDocuments({prestation_uid: prestationUid})
        const document = request.objects[0]
        this.setState({
          document: `${document.mimetype};base64,${document.document}`,
          showDocument: true,
          isModalOpen: true,
        })
      } else if (obj.document_count > 1) {
        this.selectPrestation(prestation, index)
      } else {
        throw new Error('NO_DOCUMENTS')
      }
    } catch (error) {
      this.props.actions.setNotification(
        this.props.intl.formatMessage(constants.messages.ERROR_FETCHING_DOCUMENT),
        headerConstants.NOTIF_ERROR
      )
    } finally {
      this.setState({ modalLoading: undefined })
    }
  }

  handleDocumentModal(event) {
    this.setState({
      showDocument: true,
      document: event.target.src,
    })
  }

  handleDocumentDialogConfirm() {
    this.setState({
      showDocument: false,
      isModalOpen: false,
      document: undefined,
    })
  }

  componentDidUpdate (prevProps) {
    if (this.props.prestations !== prevProps.prestations) {
      this.setState({
        prestations: this.props.prestations
      })
    }
  }

  componentWillUnmount () {
    this.props.actions.setData('selectedPrestation', undefined)
  }

  consumer (prestation) {
    return getConsumerText(prestation.consumers)
  }

  selectPrestation (prestation, index) {
    if (this.props.loading) {
      return
    }

    this.props.setLoading()
    this.props.actions.selectPrestation(prestation)
    .finally(() => this.props.setLoading())

    this.setState({ selected: index })
  }

  renderProposalBadges (prestation, role){
    const proposalBadges = [
      {
        key: 'applicantInterpreterProposals',
        count: prestation.interpreter_ongoing_proposals_count,
        requiredRole: loginConstants.APPLICANT_ROLE,
        tooltip: constants.messages.APPLICANT_PROPOSAL_MESSAGE,
        icon: 'access_time',
      },
      {
        key: 'applicantApplicantProposals',
        count: prestation.applicant_ongoing_proposals_count,
        requiredRole: loginConstants.APPLICANT_ROLE,
        tooltip: constants.messages.INTERPRETER_PROPOSAL_APPLICANT_MESSAGE,
        icon: 'hourglass_empty',
      },
      {
        key: 'interpreterApplicantProposals',
        count: prestation.applicant_ongoing_proposals_count,
        requiredRole: loginConstants.INTERPRETER_ROLE,
        tooltip: constants.messages.INTERPRETER_PROPOSAL_MESSAGE,
        icon: 'access_time',
      },
      {
        key: 'interpreterInterpreterProposals',
        count: prestation.interpreter_ongoing_proposals_count,
        requiredRole: loginConstants.INTERPRETER_ROLE,
        tooltip: constants.messages.APPLICANT_PROPOSAL_INTERPRETER_MESSAGE,
        icon: 'hourglass_empty',
      },
    ]

    return proposalBadges
        .filter(({ count, requiredRole }) => count > 0 && role === requiredRole)
        .map(({ key, count, tooltip, icon}) => (
        <div className='inline middle' key={key}>
            <Badge
            badgeContent={count}
            secondary={true}
            style={styles.proposalBadge}
            badgeStyle={styles.proposalBadgeStyle}
            >
            <IconButton tooltip={this.props.intl.formatMessage(tooltip)}>
                <FontIcon className='material-icons inline middle'>{icon}</FontIcon>
            </IconButton>
            </Badge>
        </div>
        ))
    }

  render () {
    const isStaff = this.props.user.is_staff
    const role = this.props.user.role
    return (
      <div>
        {
          this.state.prestations.map((prestation, index) => {
            const stepName = R.path(['workflow_objects', 0, 'step_name'], prestation)
            const color = constants.colors[stepName]
            const icon = constants.icons[stepName]

            let startMoment = moment(prestation.start_time)
            let endMoment = moment(prestation.end_time)

            const date = startMoment.format('DD.MM.YY')
            const startTime = startMoment.format('HH:mm')
            const endTime = endMoment.format('HH:mm')

            return (
              <div style={{height: this.props.height}}
                className={'FeedItem' + (index === this.state.selected ? ' selected' : '')}
                key={prestation.uid}
                onClick={(e) => this.selectPrestation(prestation, index)}
              >
                <FontIcon
                  className='material-icons inline middle'
                  color={color}
                  style={{minWidth: 25}}
                >
                  {icon}
                </FontIcon>

                <div
                  className='inline middle'
                  style={{
                    marginLeft: 10, 
                    marginRight: 10, 
                    minWidth: 50
                  }}
                >
                  {prestation.id}
                </div>

                <div className='FeedDateAndTime inline middle'>
                  <div className='inline middle' style={{marginLeft: 10}}>
                    <div className='FeedDate' style={{color}}>
                      {moment(prestation.start_time).format('dddd').capitalize()} { date }
                    </div>
                    {
                      !prestation.invoice_only_additional_services
                        ? <div className='FeedTime' style={{color}}>
                          {startTime} à {endTime}
                        </div>
                        : undefined
                    }
                  </div>
                </div>

                <div className='FeedInterpreter inline middle'>
                  <div className='FeedNames'>
                    {
                      (R.path(['interpreter', 'identities', 0, 'title'], prestation) || '') + ' ' +
                      (R.path(['interpreter', 'identities', 0, 'first_name'], prestation) || '') +  ' ' +
                      (R.path(['interpreter', 'identities', 0, 'last_name'], prestation) || '')
                    }
                  </div>
                  <div className='FeedLanguage'>
                    {prestation.languages[0].value}
                  </div>
                </div>
                <div className='FeedAddress inline middle'>
                  <div className='FeedAddress'>
                    {prestation.address.address}
                  </div>
                  { isStaff
                    ? <div className='FeedConsumer'>
                      { this.consumer(prestation) }
                    </div>
                    : undefined
                  }
                </div>

                {
                  prestation.is_interpreter_proposals_refused &&
                  (stepName === constants.ACCEPTED_PRESTATION_STATUS ||
                   stepName === constants.CONFIRMED_PRESTATION_STATUS)
                    ? <div className='inline middle'>
                      <IconButton
                        tooltip={this.props.intl.formatMessage(
                          role === loginConstants.INTERPRETER_ROLE
                            ? constants.messages.PROPOSAL_REFUSED_INTERPRETER_MESSAGE
                            : constants.messages.PROPOSAL_REFUSED_APPLICANT_MESSAGE
                        )}
                      >
                        <FontIcon
                          className='material-icons inline middle'
                          color={constants.colors.cancelled}
                        >
                          timer_off
                        </FontIcon>
                      </IconButton>
                    </div>
                    : undefined
                }

                {this.renderProposalBadges(prestation, role)}

                {
                  prestation.document_count
                    ? <div className='inline middle'>
                        <IconButton
                          tooltip={this.state.isModalOpen ? null : this.props.intl.formatMessage(constants.messages.DOCUMENTS_MESSAGE)}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (document.activeElement) {
                              document.activeElement.blur()
                            }
                            this.openModal(prestation.uid, prestation, index)
                          }}
                          disabled={this.state.modalLoading || prestation.disableImage}
                        >
                          {this.state.modalLoading === prestation.uid ? (
                            <CircularProgress size={24} />
                          ) : (
                            <FontIcon className='material-icons inline middle'>image</FontIcon>
                          )}
                        </IconButton>
                    </div>
                    : undefined
                }

                {
                  prestation.is_cancelled_lately
                    ? <div className='inline middle'>
                        <IconButton
                          tooltip={this.props.intl.formatMessage(constants.messages.CANCELLED_MESSAGE)}
                          tooltipPosition='top-left'
                        >
                          <FontIcon className='material-icons inline middle'>receipt_long</FontIcon>
                        </IconButton>
                    </div>
                    : undefined
                }

                {
                  prestation.validation_reminder_step === constants.INTERPRETER_REMINDER_STEP &&
                  prestation.validation_token &&
                  stepName === constants.TO_VALIDATE_PRESTATION_STATUS
                    ? <div className='inline middle'>
                      <IconButton tooltip={this.props.intl.formatMessage(constants.messages.VALIDATION_DISTANCE_SENT)}>
                        <FontIcon className='material-icons inline middle'>forward_to_inbox</FontIcon>
                      </IconButton>
                    </div>
                    : undefined
                }

                <div className='inline middle'>
                  <div style={{...styles.stepChip, color}}>
                    <Chip
                      backgroundColor={color}
                      labelColor={white}
                      labelStyle={{minWidth: 120, textAlign: 'center'}}
                    >
                      {R.path(['workflow_objects', 0, 'display_step_name'], prestation)}
                    </Chip>
                  </div>
                </div>
              </div>
            )
          })}

          <ImageDisplay
            isOpen={this.state.isModalOpen}
            onClose={this.handleDocumentDialogConfirm}
            imageUrl={this.state.document}
          />
      </div>
    )
  }
}

const styles={
  stepChip: {
    marginRight: 10,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  proposalBadge: {
    padding: '0px 15px 0px 10px',
    height: 35,
  },
  proposalBadgeStyle: {
    top: 12,
    right: 12,
    marginTop: '-10px',
    fontSize: 10,
    width: 20,
    height: 20,
  },
}

const mapStateToProps = (state) => ({
  user: state.User.user,
})

const mapActionsToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
})

export default connect(
  mapStateToProps, 
  mapActionsToProps
)(injectIntl(List))


// WEBPACK FOOTER //
// ./src/components/common/prestation/List.js